import CancelIcon from "@mui/icons-material/Cancel";
import {
  Box,
  CircularProgress,
  FormHelperText,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import axios from "axios";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { baseUrl } from "../../services";
import { handleOpenSnackBar } from "../../utils";

const useStyle = {
  iconButton: {
    position: "absolute",
    top: 0,
    right: 0,
  },

  addImageBox: {
    width: 140,
    height: 140,
    backgroundColor: "#f0f0f0",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    borderRadius: 2,
    cursor: "pointer",
    border: "1px dashed",
    borderColor: "primary.main",
  },
};

const ProductImages = ({
  inputValues,
  setInputValues,
  inputError,
  setInputError,
}) => {
  const { t } = useTranslation();
  const imageRef = useRef(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleChangeImage = (event) => {
    const file = event.target.files[0];

    const maxSize = 3 * 1024 * 1024;

    if (file?.size > maxSize) {
      return handleOpenSnackBar("error", t("validation.imageSizeLimit"));
    }

    const imageType = file?.type.split("/")[1].toLowerCase();
    const allowedFormats = ["png", "jpeg", "jpg"];
    const allowedVideoFormats = ["mp4", "mov", "avi", "mkv"];

    if (!allowedFormats.includes(imageType)) {
      return handleOpenSnackBar("error", t("validation.allowedImageFormats"));
    }

    if (allowedVideoFormats.includes(imageType)) {
      return handleOpenSnackBar("error", t("validation.videoNotAllowed"));
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        if (img.width <= 999 || img.height <= 799) {
          return handleOpenSnackBar(
            "error",
            t("validation.minImageDimensions"),
          );
        }

        const formData = new FormData();
        formData.append("image", file);
        setIsLoading(true);

        axios({
          method: "post",
          url: `${baseUrl}image/upload`,
          data: formData,
          headers: { "Content-Type": "multipart/form-data" },
        })
          .then((res) => {
            setIsLoading(false);
            imageRef.current.value = null;
            handleOpenSnackBar("success", t("success.imageUploadSuccess"));
            setInputError((prev) => ({ ...prev, images: "" }));
            setInputValues((prev) => ({
              ...prev,
              images: [...prev.images, res?.data?.url],
            }));
          })
          .catch(() => {
            setIsLoading(false);
            imageRef.current.value = null;
            handleOpenSnackBar("error", t("error.imageUploadFailed"));
          });
      };

      img.src = e.target.result;
    };

    reader.readAsDataURL(file);
  };

  const handleRemoveImage = (index) => {
    let tempArray = [...inputValues.images];
    tempArray.splice(index, 1);
    setInputValues((prev) => ({ ...prev, images: tempArray }));
  };

  const renderAddImageBox = () => {
    if (inputValues?.images?.length < 4) {
      return (
        <Box onClick={() => imageRef.current.click()} sx={useStyle.addImageBox}>
          {isLoading ? (
            <CircularProgress size={20} />
          ) : (
            <Typography variant="h5" color="primary.main">
              +
            </Typography>
          )}
        </Box>
      );
    }
  };

  return (
    <>
      <Stack direction={"row"} flexWrap={"wrap"} gap={2}>
        {inputValues?.images?.map((img, index) => (
          <Box
            key={index}
            position={"relative"}
            width={140}
            height={140}
            borderRadius={2}
            border="1px dashed"
            borderColor="primary.main"
            p={1}
          >
            <Box
              width={"100%"}
              height={"100%"}
              sx={{
                background: `url(${img})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
                backgroundRepeat: "no-repeat",
              }}
            />
            <IconButton
              onClick={() => handleRemoveImage(index)}
              sx={useStyle.iconButton}
              size="small"
            >
              <CancelIcon htmlColor="red" fontSize="xs" />
            </IconButton>
          </Box>
        ))}
        {renderAddImageBox()}
      </Stack>
      {inputError?.images && (
        <FormHelperText error sx={{ marginTop: 0.3 }}>
          {t(inputError.images)}
        </FormHelperText>
      )}
      <input
        type="file"
        accept="image/*"
        ref={imageRef}
        style={{ display: "none" }}
        onChange={handleChangeImage}
      />
    </>
  );
};

export const UploaderProductImages = ProductImages;
export default ProductImages;
